import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Check, CheckCircle2, Globe, Loader2, LogOut, Upload } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient, useDolfinAuth } from "@/components/DolfinProvider";
import { toastApiError, apiErrorMessage } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";
import { BackLink, PageContainer, PageHeader } from "@/components/PagePrimitives";
import type { components } from "@dolfin/api-admin";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { Eyebrow, LoadingState } from "@/components/PageUi";

type Registration = components["schemas"]["EInvoicingRegistrationResponse"];
type Status = components["schemas"]["EInvoicingRegistrationStatus"];
type Party = components["schemas"]["EInvoicingPartyResponse"];

/** The registration identifies the organisation on the network (Peppol, or SDI in Italy), so it isn't payables'. */
export const E_INVOICING_INTRO =
  "Register this organisation for e-invoicing so your suppliers can send you e-invoices electronically.";

/** Null until the backend picks a network (from the tax country); re-deriving that here would duplicate the rule. */
function networkName(network: Registration["network"]): string | null {
  if (network === "Sdi") return "SDI";
  if (network === "Peppol") return "Peppol";
  return null;
}

/** Poll only while the provider is working. Nothing else here changes without the user acting. */
const POLL_INTERVAL_MS = 4000;

function StatusBadge({ status }: { status: Status }) {
  switch (status) {
    case "Registered":
      return (
        <Badge variant="outline" className="border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="size-3" />
          Registered
        </Badge>
      );
    case "ActionRequired":
      return (
        <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400">
          <AlertTriangle className="size-3" />
          Action required
        </Badge>
      );
    case "Processing":
    case "Submitted":
      return (
        <Badge variant="outline" className="border-blue-500/40 bg-blue-500/10 text-blue-700 dark:text-blue-400">
          <Loader2 className="size-3 animate-spin" />
          {status === "Processing" ? "Verifying" : "Submitted"}
        </Badge>
      );
    case "Failed":
      return (
        <Badge variant="outline" className="border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-400">
          <AlertTriangle className="size-3" />
          Failed
        </Badge>
      );
    case "Unregistering":
      return (
        <Badge variant="outline" className="border-blue-500/40 bg-blue-500/10 text-blue-700 dark:text-blue-400">
          <Loader2 className="size-3 animate-spin" />
          Unregistering
        </Badge>
      );
    case "Unregistered":
      return <Badge variant="outline">Unregistered</Badge>;
    default:
      return <Badge variant="outline">Not started</Badge>;
  }
}

/** The proof step only exists on networks that ask for one: SDI goes straight from details to verification. */
function RegistrationSteps({ status, network }: { status: Status; network: string | null }) {
  const withProof = network === "Peppol" || status === "ActionRequired";
  const steps = ["Company details", ...(withProof ? ["Proof of ownership"] : []), "Network verification", "Registered"];
  const current =
    status === "Registered"
      ? steps.length
      : status === "Processing" || status === "Submitted"
        ? steps.indexOf("Network verification")
        : status === "ActionRequired"
          ? 1
          : 0;
  const failed = status === "Failed";

  return (
    <ol className="grid gap-2 @xl:grid-flow-col @xl:auto-cols-fr">
      {steps.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li
            key={label}
            className={cn(
              "flex items-center gap-2 rounded-lg border px-3 py-2 text-xs",
              active && !failed && "border-accent-blue/40 bg-accent-blue/5",
              active && failed && "border-destructive/40 bg-destructive/5",
            )}
          >
            <span
              aria-hidden
              className={cn(
                "grid size-5 shrink-0 place-items-center rounded-full border text-[10px] font-semibold tabular-nums",
                done && "border-emerald-500 bg-emerald-500 text-white",
                active && !failed && "border-accent-blue text-accent-blue",
                active && failed && "border-destructive text-destructive",
                !done && !active && "text-muted-foreground",
              )}
            >
              {done ? <Check className="size-3" /> : i + 1}
            </span>
            <span className={cn(done || active ? "font-medium text-foreground" : "text-muted-foreground")}>
              {label}
            </span>
            <span className="sr-only">{done ? "(done)" : active ? "(current)" : ""}</span>
          </li>
        );
      })}
    </ol>
  );
}

const FIELDS: { key: keyof Party; label: string; required?: boolean }[] = [
  { key: "legalName", label: "Registered legal name", required: true },
  { key: "taxNumber", label: "VAT / tax number", required: true },
  { key: "companyRegistrationNumber", label: "Company registration number" },
  { key: "addressLine1", label: "Address line 1", required: true },
  { key: "addressLine2", label: "Address line 2" },
  { key: "city", label: "City", required: true },
  { key: "addressRegion", label: "Region / county" },
  { key: "postalCode", label: "Postal code", required: true },
  { key: "country", label: "Country (ISO 2-letter)", required: true },
  { key: "contactEmail", label: "Contact email", required: true },
];

/** No page chrome, so a host can drop it into a tab. */
export function EInvoicingSettings() {
  const api = useDolfinClient();
  const { organisationId, authFetch } = useDolfinAuth();

  const [registration, setRegistration] = useState<Registration | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmingUnregister, setConfirmingUnregister] = useState(false);
  const [unregistering, setUnregistering] = useState(false);
  const [form, setForm] = useState<Party>({});
  // The form is seeded from the server exactly once. Re-seeding on every poll would overwrite
  // whatever the user was mid-way through typing.
  const seeded = useRef(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!organisationId) return;
    const { data, error } = await api.GET("/v1/e-invoicing", {});
    if (error) {
      toastApiError(error, "Failed to load e-invoicing status.");
    } else if (data) {
      setRegistration(data);
      if (!seeded.current) {
        setForm(data.party ?? {});
        seeded.current = true;
      }
    }
    setLoading(false);
  }, [api, organisationId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Processing, Submitted and Unregistering are waiting on the provider. ActionRequired is waiting on
  // the user, and polling through it would be a spinner that never resolves on its own.
  const status = registration?.status ?? "NotStarted";
  const isWaitingOnProvider = status === "Processing" || status === "Submitted" || status === "Unregistering";

  useEffect(() => {
    if (!isWaitingOnProvider) return;
    const id = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isWaitingOnProvider, load]);

  async function submit() {
    if (!organisationId) return;
    setSubmitting(true);
    const { data, error } = await api.POST("/v1/e-invoicing/registration", {
      body: form,
    });
    setSubmitting(false);

    if (error) {
      toastApiError(error, "Registration could not be submitted.");
      return;
    }
    if (data) {
      setRegistration(data);
      // Peppol stops for proof of ownership; SDI asks for nothing and goes straight to verification.
      toast.success(
        data.status === "ActionRequired"
          ? "Submitted. Upload your proof of ownership to finish."
          : "Submitted. The network is verifying your registration.",
      );
    }
  }

  async function unregister() {
    if (!organisationId) return;
    setUnregistering(true);
    const { data, error } = await api.DELETE("/v1/e-invoicing/registration", {});
    setUnregistering(false);
    setConfirmingUnregister(false);

    if (error) {
      toastApiError(error, "Could not unregister from e-invoicing.");
      return;
    }
    if (data) {
      setRegistration(data);
      toast.success("Unregistering. The network is withdrawing this organisation.");
    }
  }

  async function uploadProof(file: File) {
    if (!organisationId) return;
    setUploading(true);
    try {
      // Multipart, which openapi-fetch doesn't model.
      const body = new FormData();
      body.append("file", file);
      const response = await authFetch(
        "/v1/e-invoicing/registration/documents",
        { method: "POST", body },
      );
      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        toast.error(apiErrorMessage(payload, "Failed to upload the document."));
        return;
      }
      setRegistration(payload as Registration);
      toast.success("Uploaded. The network is verifying your registration.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  if (loading) {
    return <LoadingState label="Loading e-invoicing..." />;
  }

  // Not keyed off `canSubmit`: an incomplete organisation is exactly the one that needs to type here.
  // Completeness gates the submit button (`missingRequired`).
  const canEdit = status !== "Registered" && !isWaitingOnProvider;
  const showForm = status === "NotStarted" || status === "Failed" || status === "Unregistered";
  const missingRequired = FIELDS.filter((f) => f.required && !form[f.key]?.toString().trim());
  const network = networkName(registration?.network);

  return (
    <div className="@container flex flex-col gap-4">
      <section className="rounded-xl border bg-card/60 p-5 backdrop-blur-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="grid size-10 shrink-0 place-items-center rounded-xl border bg-muted/50">
              <Globe className="size-5 text-muted-foreground" />
            </span>
            <div className="font-semibold">{network ? `${network} registration` : "E-invoicing registration"}</div>
          </div>
          <StatusBadge status={status} />
        </div>

        <p className="mt-3 text-sm text-muted-foreground">
          {status === "Registered"
            ? "This organisation can receive e-invoices."
            : status === "Unregistering"
              ? "The network is withdrawing this organisation. It can still receive e-invoices until that finishes."
            : status === "Unregistered"
              ? "Unregistered. This organisation no longer receives e-invoices, and can register again below."
            : status === "Failed"
              ? "The network rejected this registration. Correct the details below and try again."
            : status === "ActionRequired"
              ? "One more step: upload a document proving you own this company."
            : isWaitingOnProvider
              ? "The network is verifying your registration. This page updates itself."
              : "Not registered yet."}
        </p>

        {status !== "Unregistering" && status !== "Unregistered" ? (
          <div className="mt-4">
            <RegistrationSteps status={status} network={network} />
          </div>
        ) : null}

        {registration?.participant && (
          <dl className="mt-5 grid grid-cols-1 gap-px overflow-hidden rounded-lg border bg-border @lg:grid-cols-2">
            <div className="bg-card p-3">
              {/* On SDI the address IS the Partita IVA — there is no participant scheme to show. */}
              <dt>
                <Eyebrow>{network === "SDI" ? "SDI address (Partita IVA)" : `${network ?? "E-invoicing"} address`}</Eyebrow>
              </dt>
              <dd className="mt-1 font-mono text-sm break-all">
                {network === "SDI"
                  ? registration.participant.code
                  : `${registration.participant.scheme}:${registration.participant.code}`}
              </dd>
            </div>
            <div className="bg-card p-3">
              <dt>
                <Eyebrow>Registered</Eyebrow>
              </dt>
              <dd className="mt-1 text-sm tabular-nums">
                {registration.registeredAt ? formatDateTime(registration.registeredAt) : "Not yet"}
              </dd>
            </div>
          </dl>
        )}

        {!!registration?.faults?.length && (
          <div className="mt-4 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-destructive">
              <AlertTriangle className="size-4" /> What the network said
            </div>
            <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
              {registration.faults.map((f, i) => <li key={i}>{f.message ?? f.code}</li>)}
            </ul>
          </div>
        )}

        {status === "ActionRequired" && (
          <div className="mt-4 space-y-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
            {registration?.requiredActions?.map((a) => (
              <p key={a.code} className="text-sm">{a.detail}</p>
            ))}
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void uploadProof(file);
              }}
            />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? <Spinner className="size-4" /> : <Upload className="size-4" />}
              {uploading ? "Uploading..." : "Upload proof of ownership (PDF)"}
            </Button>
          </div>
        )}

        {status === "Registered" && (
          <div className="mt-4 flex flex-col gap-3 border-t pt-4 @lg:flex-row @lg:items-center @lg:justify-between">
            <p className="text-xs text-muted-foreground">
              Unregistering stops this organisation receiving e-invoices and releases its network address.
              Its tax number and country can then be changed, and it can register again.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => setConfirmingUnregister(true)}
              disabled={unregistering}
            >
              <LogOut className="size-4" />
              Unregister
            </Button>
          </div>
        )}
      </section>

      {showForm && (
        <section className="rounded-xl border bg-card/60 p-5 backdrop-blur-sm">
          <Eyebrow>Registration details</Eyebrow>
          <p className="mt-1 text-sm text-muted-foreground">
            Prefilled from this organisation. Anything you change here applies to the registration only.
          </p>
          <div className="mt-4 grid gap-4 @lg:grid-cols-2">
            {FIELDS.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={f.key}>
                  {f.label}
                  {f.required && <span className="ml-0.5 text-red-500">*</span>}
                </Label>
                <Input
                  id={f.key}
                  value={form[f.key] ?? ""}
                  disabled={!canEdit}
                  onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                />
              </div>
            ))}
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3 border-t pt-4">
            <Button onClick={() => void submit()} disabled={submitting || !canEdit || missingRequired.length > 0}>
              {submitting && <Spinner className="size-4" />}
              {status === "Failed" ? "Try again" : status === "Unregistered" ? "Register again" : "Register for e-invoicing"}
            </Button>
            {missingRequired.length > 0 && (
              <span className="text-sm text-muted-foreground">
                Still needed: {missingRequired.map((f) => f.label).join(", ")}
              </span>
            )}
          </div>
        </section>
      )}

      <AlertDialog open={confirmingUnregister} onOpenChange={(open) => !unregistering && setConfirmingUnregister(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unregister from e-invoicing?</AlertDialogTitle>
            <AlertDialogDescription>
              {network ?? "The network"} will stop delivering e-invoices to this organisation, and its address
              {registration?.participant ? ` (${registration.participant.code})` : ""} is released. Suppliers who send
              to it afterwards will not reach you. You can register again at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unregistering}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={unregistering}
              onClick={(e) => {
                // Keep the dialog open until the request answers.
                e.preventDefault();
                void unregister();
              }}
            >
              {unregistering && <Spinner className="size-4" />}
              Unregister
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export interface EInvoicingSettingsPageProps {
  onBack?: () => void;
  /** Label for the back link. Defaults to the organisation page the sandbox comes from. */
  backLabel?: string;
}

/** Standalone-page form of {@link EInvoicingSettings}, for hosts that give it a route of its own. */
export function EInvoicingSettingsPage({ onBack, backLabel = "Back to organisation" }: EInvoicingSettingsPageProps) {
  return (
    <PageContainer size="narrow">
      {onBack && <BackLink onClick={onBack}>{backLabel}</BackLink>}
      <PageHeader eyebrow="Organisation" title="E-invoicing" subtitle={E_INVOICING_INTRO} />
      <EInvoicingSettings />
    </PageContainer>
  );
}
